/**
 * La fiche de préparation d'un jour (lot PL4, ADR-0015 ; lue des interventions depuis ADR-0016) : les pièces à sortir du magasin AVANT de partir, intervention par intervention,
 * puis le total du jour — la même référence de deux machines est additionnée, une seule ligne à aller chercher. Une page A4,
 * des cases à cocher pour le magasin, deux lignes de signature. Aucun prix, aucun stock : on liste, on ne réserve rien.
 *
 * Les pièces viennent de la gamme d'entretien de chaque machine, au palier choisi sur l'intervention : rien n'est saisi ici. Une machine
 * sans gamme, sans palier choisi ou au palier inconnu est SIGNALÉE en haut de la fiche — on ne croit pas une intervention complète quand
 * elle ne l'est pas. On y arrive depuis le planning (« Fiche de préparation ») ; la fiche se règle sur un jour et, au choix, un
 * technicien (`?jour=…&technicien=…`).
 *
 * Même principe d'impression que le bon et la gamme : la page EST le document, la règle `@page` est ajoutée tant qu'elle est
 * affichée (jamais une page nommée), et le titre de la page est le nom du PDF.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, api, type Preparation, type Technicien } from '../api/client';
import { useIdentiteEspace } from '../domaine/identite-espace';
import { aujourdhuiLocal } from '../domaine/interventions';
import { CONSEIL_EN_TETES_NAVIGATEUR, installerPageFiche } from '../domaine/page-impression';
import { dureeFr, jourValide, lundiDe } from '../domaine/planning';
import {
  PHRASE_ETAT_PIECES,
  enteteMachineFiche,
  ligneDuTotal,
  lignesDeLaMachine,
  lireParametresFiche,
  nomFiche,
  quantiteAffichee,
  signalementsFiche,
  sousTitreFiche,
} from '../domaine/planning-pieces';

export function FichePreparationPage() {
  const [params, setParams] = useSearchParams();
  const lus = lireParametresFiche(params);
  const jour = lus.jour ?? aujourdhuiLocal();
  const technicienId = lus.technicien;
  const identite = useIdentiteEspace();

  const [fiche, setFiche] = useState<Preparation | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [techniciens, setTechniciens] = useState<readonly Technicien[]>([]);

  useEffect(() => {
    let actif = true;
    api
      .techniciensPlanning()
      .then((r) => {
        if (actif) setTechniciens(r.items.filter((t) => !t.archived));
      })
      .catch(() => undefined); // sans la liste, la fiche reste lisible pour tous les techniciens
    return () => {
      actif = false;
    };
  }, []);

  useEffect(() => {
    setFiche(null);
    setErreur(null);
    let actif = true;
    api
      .planningPreparationInterventions(jour, technicienId ?? undefined)
      .then((f) => {
        if (actif) setFiche(f);
      })
      .catch((e: unknown) => {
        if (actif) setErreur(e instanceof ApiError ? e.message : 'Fiche illisible.');
      });
    return () => {
      actif = false;
    };
  }, [jour, technicienId]);

  // Le titre de la page devient le nom du document : c'est le nom que Chrome et Edge proposent au PDF.
  const nom = fiche ? nomFiche(fiche) : null;
  useEffect(() => {
    if (!nom) return;
    const avant = window.document.title;
    window.document.title = nom;
    return () => {
      window.document.title = avant;
    };
  }, [nom]);

  // Marges de la feuille : une règle @page ordinaire, ajoutée tant que cette page est affichée (domaine/page-impression.ts).
  useEffect(() => installerPageFiche(window.document), []);

  const signalements = useMemo(() => (fiche ? signalementsFiche(fiche) : []), [fiche]);

  const regler = (changement: { jour?: string; technicien?: string | null }) => {
    const p = new URLSearchParams(params);
    if (changement.jour !== undefined && jourValide(changement.jour)) p.set('jour', changement.jour);
    if (changement.technicien !== undefined) {
      if (changement.technicien === null || changement.technicien === '') p.delete('technicien');
      else p.set('technicien', changement.technicien);
    }
    setParams(p, { replace: true });
  };

  return (
    <>
      {/* Tout ce bloc disparaît à l'impression : seule la feuille sort. */}
      <div className="barre-impression">
        <div className="fil-ariane">
          <Link to={`/planning/interventions?semaine=${lundiDe(jour)}`}>← Retour au planning</Link>
        </div>

        <div className="entete-page">
          <div>
            <h1>Fiche de préparation</h1>
            <p>Les pièces à sortir du magasin avant de partir : celles du palier choisi sur chaque intervention, sans prix.</p>
          </div>
          <div className="actions">
            <button disabled={fiche === null} onClick={() => window.print()}>
              Imprimer / Enregistrer en PDF
            </button>
          </div>
        </div>

        <div className="saisie-ligne fiche-reglages">
          <div className="champ">
            <label htmlFor="fiche-jour">Jour</label>
            <input id="fiche-jour" type="date" value={jour} onChange={(e) => regler({ jour: e.target.value })} />
          </div>
          <div className="champ">
            <label htmlFor="fiche-technicien">Technicien</label>
            <select id="fiche-technicien" value={technicienId ?? ''} onChange={(e) => regler({ technicien: e.target.value })}>
              <option value="">Tous les techniciens</option>
              {techniciens.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {erreur && <div className="message erreur">{erreur}</div>}
        {fiche === null && erreur === null && <div className="message info">Lecture des interventions du jour…</div>}
        <div className="message info">{CONSEIL_EN_TETES_NAVIGATEUR}</div>
      </div>

      {fiche && (
        <div className="fiche-feuille">
          <header className="fiche-haut">
            <h2>Fiche de préparation</h2>
            {identite?.nom && <span className="fiche-entreprise">{identite.nom}</span>}
          </header>
          <p className="fiche-sous">{sousTitreFiche(fiche)}</p>

          {signalements.length > 0 && (
            <div className="fiche-signale">
              <strong>À savoir avant de préparer</strong>
              <ul>
                {signalements.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
          )}

          {fiche.visites.length === 0 && <p className="fiche-vide">Aucune intervention ouverte ce jour-là{fiche.technicien ? ' pour ce technicien' : ''} : rien à préparer.</p>}

          {fiche.visites.map((v) => (
            <section key={v.id} className="fiche-visite">
              <div className="fiche-visite-haut">
                <strong>
                  {v.ordre}. {v.client}
                  {v.site ? ` — ${v.site}` : ''}
                  {v.ville ? ` · ${v.ville}` : ''}
                </strong>
                <span>
                  {[
                    v.nature,
                    v.technicians.length > 0 ? v.technicians.map((t) => t.name).join(', ') : 'sans technicien',
                    v.durationMinutes === null ? null : dureeFr(v.durationMinutes),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
              {v.machines.length === 0 && <p className="fiche-vide">Aucune machine indiquée sur cette intervention : aucune pièce listée.</p>}
              {v.machines.map(({ machine, pieces }) => (
                <div key={pieces.lineId} className="fiche-machine">
                  <div className="fiche-machine-titre">{enteteMachineFiche(machine, pieces)}</div>
                  {lignesDeLaMachine(pieces).length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th className="fiche-col-case" />
                          <th className="fiche-col-ref">Référence</th>
                          <th>Désignation</th>
                          <th className="fiche-col-qte">Qté</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lignesDeLaMachine(pieces).map((p, i) => (
                          <tr key={`${p.reference ?? ''}|${p.designation}|${i}`}>
                            <td className="fiche-col-case">
                              <span className="fiche-case" aria-hidden="true" />
                            </td>
                            <td className="fiche-col-ref">{p.reference ?? '—'}</td>
                            <td>{p.designation}</td>
                            <td className="fiche-col-qte">{quantiteAffichee(p.quantite, p.unite)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="fiche-vide">{PHRASE_ETAT_PIECES[pieces.etat]}</p>
                  )}                </div>
              ))}
            </section>
          ))}

          {fiche.total.length > 0 && (
            <section className="fiche-total">
              <h3>Total à sortir pour la journée — les mêmes références sont additionnées</h3>
              <table>
                <thead>
                  <tr>
                    <th className="fiche-col-case" />
                    <th className="fiche-col-ref">Référence</th>
                    <th>Désignation</th>
                    <th className="fiche-col-pour">Pour</th>
                    <th className="fiche-col-qte">Qté</th>
                  </tr>
                </thead>
                <tbody>
                  {fiche.total.map((t, i) => {
                    const l = ligneDuTotal(t);
                    return (
                      <tr key={`${t.reference ?? ''}|${t.designation}|${t.unite ?? ''}|${i}`}>
                        <td className="fiche-col-case">
                          <span className="fiche-case" aria-hidden="true" />
                        </td>
                        <td className="fiche-col-ref">{l.reference}</td>
                        <td>{l.designation}</td>
                        <td className="fiche-col-pour">{l.pour}</td>
                        <td className="fiche-col-qte">
                          <strong>{l.quantite}</strong>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          )}

          <div className="fiche-pied">
            <div>Préparé par : ……………………………… le ………………</div>
            <div>Vérifié par : ……………………………… le ………………</div>
          </div>
        </div>
      )}
    </>
  );
}
